/**
 * One source of truth for an interviewer being shaped: the config on the page,
 * Rio's last preview, the brief-Rio conversation, and what just changed.
 *
 * Local edits (toggles, minutes, chips) apply at once. A plain-language brief
 * goes to Rio, comes back as a proposed config, and is applied straight away
 * so the recruiter sees it; Keep accepts it and Undo puts the card back as it
 * was. Sending another brief, or editing the card, keeps the last one.
 *
 * While the posting is still being written, Rio redrafts from it (debounced),
 * but only until the recruiter changes something: after that a new draft would
 * throw their work away, so the card offers to redraft instead.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  AppliedChange,
  InterviewerConfig,
  InterviewerPreview,
  Posting,
  RejectedChange,
} from '../services/interviewer.types';
import { InterviewerError } from '../services/interviewer';
import { useInterviewerService } from './serviceContext';
import { changedRegions, derivePreview, postingReady } from './model';

export const DRAFT_DEBOUNCE_MS = 900;
export const HIGHLIGHT_MS = 1600;

export type DraftStatus = 'empty' | 'drafting' | 'ready' | 'error';

export interface BriefMessage {
  id: number;
  from: 'you' | 'rio';
  text: string;
  status: 'thinking' | 'done' | 'error';
  applied?: AppliedChange[];
  rejected?: RejectedChange[];
  /** Only on a reply that changed the card. */
  decision?: 'pending' | 'kept' | 'undone';
  /** The brief to send again after an error. */
  retry?: string;
}

interface Snapshot {
  config: InterviewerConfig;
  base: InterviewerPreview | null;
  touched: boolean;
  messageId: number;
  regions: Set<string>;
}

export interface UseInterviewerOptions {
  posting: Posting;
  /** Redraft from the posting as it changes, until the recruiter edits. */
  autoDraft?: boolean;
  /** Off: nothing is requested at all. */
  enabled?: boolean;
}

export const useInterviewer = ({
  posting,
  autoDraft = false,
  enabled = true,
}: UseInterviewerOptions) => {
  const service = useInterviewerService();
  const [config, setConfig] = useState<InterviewerConfig | null>(null);
  const [base, setBase] = useState<InterviewerPreview | null>(null);
  const [status, setStatus] = useState<DraftStatus>('empty');
  const [draftError, setDraftError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [messages, setMessages] = useState<BriefMessage[]>([]);
  const [busy, setBusy] = useState(false);
  const [highlight, setHighlight] = useState<Set<string>>(new Set());
  const [draftedFor, setDraftedFor] = useState<string | null>(null);

  const snapshot = useRef<Snapshot | null>(null);
  const draftTicket = useRef(0);
  const messageId = useRef(0);
  const highlightTimer = useRef<ReturnType<typeof setTimeout>>();
  const postingRef = useRef(posting);
  postingRef.current = posting;

  useEffect(() => () => clearTimeout(highlightTimer.current), []);

  const flash = useCallback((regions: Set<string>) => {
    clearTimeout(highlightTimer.current);
    setHighlight(regions);
    highlightTimer.current = setTimeout(
      () => setHighlight(new Set()),
      HIGHLIGHT_MS,
    );
  }, []);

  const settle = useCallback((decision: 'kept' | 'undone') => {
    const pending = snapshot.current;
    if (!pending) return;
    snapshot.current = null;
    setMessages((all) =>
      all.map((m) => (m.id === pending.messageId ? { ...m, decision } : m)),
    );
  }, []);

  const draft = useCallback(
    async (from: Posting = postingRef.current) => {
      if (!enabled) return;
      const ticket = ++draftTicket.current;
      setStatus('drafting');
      setDraftError(null);
      try {
        const result = await service.draft(from);
        if (ticket !== draftTicket.current) return;
        snapshot.current = null;
        setConfig(result.config);
        setBase(result.preview);
        setTouched(false);
        setDraftedFor(JSON.stringify(from));
        setStatus('ready');
      } catch (error) {
        if (ticket !== draftTicket.current) return;
        setDraftError(
          error instanceof InterviewerError
            ? error.message
            : "Rio couldn't draft the interviewer. Try again.",
        );
        setStatus('error');
      }
    },
    [enabled, service],
  );

  // Redraft as the posting is written, while the draft is still Rio's own.
  const postingKey = JSON.stringify(posting);
  useEffect(() => {
    if (!enabled || !autoDraft || touched || !postingReady(posting)) return;
    const timer = setTimeout(() => {
      draft(postingRef.current);
    }, DRAFT_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postingKey, enabled, autoDraft, touched]);

  /** Load a saved interviewer (the job page), replacing whatever is shown. */
  const load = useCallback(
    (next: InterviewerConfig, preview?: InterviewerPreview) => {
      draftTicket.current += 1;
      snapshot.current = null;
      setConfig(next);
      if (preview) setBase(preview);
      setTouched(false);
      setStatus('ready');
      setDraftError(null);
    },
    [],
  );

  /** A local edit from the card. */
  const update = useCallback(
    (change: (current: InterviewerConfig) => InterviewerConfig) => {
      if (busy) return;
      setConfig((current) => {
        if (!current) return current;
        const next = change(current);
        return next === current ? current : next;
      });
      settle('kept');
      setTouched(true);
    },
    [busy, settle],
  );

  const send = useCallback(
    async (instruction: string) => {
      const text = instruction.trim();
      if (!text || !config || busy || !enabled) return;
      settle('kept');
      const youId = ++messageId.current;
      const rioId = ++messageId.current;
      setMessages((all) => [
        ...all,
        { id: youId, from: 'you', text, status: 'done' },
        { id: rioId, from: 'rio', text: '', status: 'thinking' },
      ]);
      setBusy(true);
      const before = { config, base, touched };
      try {
        const result = await service.edit(config, text, postingRef.current);
        const changed = result.applied.length > 0;
        if (changed) {
          const regions = changedRegions(result.diff, result.config);
          snapshot.current = { ...before, messageId: rioId, regions };
          setConfig(result.config);
          setBase(result.preview);
          setTouched(true);
          flash(regions);
        }
        setMessages((all) =>
          all.map((m) =>
            m.id === rioId
              ? {
                  ...m,
                  status: 'done',
                  applied: result.applied,
                  rejected: result.rejected,
                  decision: changed ? 'pending' : undefined,
                }
              : m,
          ),
        );
      } catch (error) {
        const reason =
          error instanceof InterviewerError
            ? error.kind === 'validation' && error.errors.length > 1
              ? error.errors.map((e) => e.message).join(' ')
              : error.message
            : "Rio couldn't make that change. Try again.";
        setMessages((all) =>
          all.map((m) =>
            m.id === rioId
              ? { ...m, status: 'error', text: reason, retry: text }
              : m,
          ),
        );
      } finally {
        setBusy(false);
      }
    },
    [base, busy, config, enabled, flash, service, settle, touched],
  );

  const keep = useCallback(() => settle('kept'), [settle]);

  const undo = useCallback(() => {
    const pending = snapshot.current;
    if (!pending) return;
    setConfig(pending.config);
    setBase(pending.base);
    setTouched(pending.touched);
    flash(pending.regions);
    settle('undone');
  }, [flash, settle]);

  const preview = useMemo(
    () => (config ? derivePreview(config, base, posting.skills) : null),
    [config, base, posting.skills],
  );

  // The recruiter has edited a draft whose posting has since changed.
  const postingChanged =
    touched &&
    draftedFor !== null &&
    draftedFor !== postingKey &&
    postingReady(posting);

  return {
    config,
    preview,
    status,
    postingChanged,
    draftError,
    touched,
    messages,
    busy,
    highlight,
    draft,
    load,
    update,
    send,
    keep,
    undo,
  };
};

export type InterviewerState = ReturnType<typeof useInterviewer>;
